import { Popover } from '@mui/material';
import Image from 'next/legacy/image';
import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';
import { mutate as globalMutate } from 'swr';
import useSWRInfinite from 'swr/infinite';

import { handleAxiosError } from '@helpers/handleAxiosError';
import { useGetMembersCounter } from '@hooks/requests/useGetMembersCounter';
import { useGetTeamsCounter } from '@hooks/requests/useGetTeamsCounter';
import useApiUrl from '@hooks/useApiUrl';
import useBoolean from '@hooks/useBoolean';
import useStoredHeader from '@hooks/useStoredHeader';
import useUserType from '@hooks/useUserType';
import { theme } from '@theme/theme';
import getClientOrganizations, {
    Organization,
} from 'api/organizations/getClientOrganizations';
import switchClientOrganization from 'api/switchClientOrganization';
import { isAllowedToCreateOrganization } from 'helpers/teamCollaboration/getPermissions';
import { mutationKeys, useMutationsStore } from 'state/useMutateHandlers';
import { useUserData } from 'state/userData';

import Loader from 'components/Loader';
import ExpandMoreIcon from 'components/SVGIcons/ExpandMoreIcon';
import PlusIcon from 'components/SVGIcons/PlusIcon';

import {
    CompanyImageWrapper,
    CreateNewOrganizationButton,
    PopoverBox,
    SelectButton,
    StyledMenuItem,
    TruncatedText,
} from './OrganizationSelect.styled';

interface Props {
    setIsOpenNewOrganization: Dispatch<SetStateAction<boolean>>;
}

interface GetOrganizationsFetcherParams {
    key: string;
    params: {
        offset: number;
    };
}

const LIMIT = 10;

const OrganizationSelect: React.FC<Props> = ({ setIsOpenNewOrganization }) => {
    const { isClient } = useUserType();
    const { user, setUserData } = useUserData();
    const apiURL = useApiUrl();
    const { mutate: mutateMembersCounter } = useGetMembersCounter();
    const { mutate: mutateTeamsCounter } = useGetTeamsCounter();
    const { mutateHeader } = useStoredHeader();
    const { setMutations } = useMutationsStore();

    const activeOrganization = user.activeCompany;
    const withCompanyLogo = !!activeOrganization?.photo?.path;
    const observer = useRef<IntersectionObserver | null>(null);
    const { value: isLastPage, setTrue: setIsLastPage } = useBoolean(false);

    const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
    const handleClick = (target: HTMLDivElement) => setAnchorEl(target);
    const open = Boolean(anchorEl);
    const handleClose = () => setAnchorEl(null);

    const fetcher = async ({ params }: GetOrganizationsFetcherParams) => {
        const { data } = await getClientOrganizations({
            limit: LIMIT,
            ...params,
        });
        return data.results;
    };

    const getKey = (pageIndex: number, previousPageData: Organization[]) => {
        // Reached the end
        if (previousPageData && !previousPageData.length) {
            setIsLastPage();
            return null;
        }

        return {
            key: `${pageIndex * LIMIT}`,
            params: {
                offset: pageIndex * LIMIT,
            },
        };
    };

    const { data, isLoading, mutate, setSize, size } = useSWRInfinite(
        getKey,
        fetcher,
        {
            revalidateFirstPage: false,
            revalidateAll: false,
            persistSize: true,
        }
    );

    useEffect(() => {
        setMutations(mutationKeys.organizations, mutate);
    }, [mutate]);

    const isEnableToCreateOrganization = isAllowedToCreateOrganization(
        user.groupRoles
    );

    const organizations = data?.flat();

    const lastPostElementRef = useCallback(
        (node: HTMLLIElement) => {
            if (isLoading || isLastPage) return;
            if (observer.current) observer.current.disconnect();
            observer.current = new IntersectionObserver(
                (entries) => {
                    if (entries[0]?.isIntersecting && data?.length) {
                        const lastPage = data.at(data.length - 1);
                        if (lastPage && lastPage.length === LIMIT) {
                            setSize(size + 1);
                        }
                    }
                },
                { threshold: 0.5 }
            );
            if (node) observer.current.observe(node);
        },
        [isLoading, isLastPage, data]
    );

    const switchHandler = async (el: Organization) => {
        setAnchorEl(null);

        try {
            await switchClientOrganization({ slug: el.slug });
            setUserData({ activeCompany: el });
            globalMutate('/me/');
            mutateMembersCounter();
            mutateTeamsCounter();
            mutateHeader();
        } catch (error) {
            handleAxiosError(error);
        }
    };

    if (!isClient) return null;

    const handleCreateNewOrganization = () => {
        setIsOpenNewOrganization?.(true);
        setAnchorEl(null);
    };

    return (
        <>
            <SelectButton
                onClick={(e) => handleClick(e.currentTarget)}
                $isOpened={open}>
                {withCompanyLogo && (
                    <CompanyImageWrapper>
                        <Image
                            src={`${apiURL}${activeOrganization?.photo?.path}`}
                            width={28}
                            height={28}
                            layout="fill"
                            objectFit="contain"
                            alt="Company logo"
                        />
                    </CompanyImageWrapper>
                )}

                <TruncatedText>{activeOrganization?.title}</TruncatedText>

                <ExpandMoreIcon
                    width={12}
                    style={{ flexShrink: 0 }}
                    rotate={open}
                />
            </SelectButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}>
                <PopoverBox>
                    {isLoading && <Loader height="100px" size={20} />}

                    {organizations?.map((el, index) => {
                        const isSelected = el.slug === activeOrganization?.slug;

                        const Logo = () => {
                            const withLogo = el.photo?.path;

                            if (!withLogo) return null;

                            return (
                                <CompanyImageWrapper>
                                    <Image
                                        src={`${apiURL}${el.photo.path}`}
                                        width={28}
                                        height={28}
                                        layout="fill"
                                        objectFit="contain"
                                        alt="Company logo"
                                    />
                                </CompanyImageWrapper>
                            );
                        };

                        if (index === organizations.length - 1) {
                            return (
                                <StyledMenuItem
                                    key={el.slug}
                                    $isSelected={isSelected}
                                    ref={lastPostElementRef}
                                    onClick={() => {
                                        switchHandler(el);
                                    }}>
                                    <Logo />

                                    {el.title}
                                </StyledMenuItem>
                            );
                        }

                        return (
                            <StyledMenuItem
                                key={el.slug}
                                $isSelected={isSelected}
                                onClick={() => {
                                    switchHandler(el);
                                }}>
                                <Logo />

                                {el.title}
                            </StyledMenuItem>
                        );
                    })}

                    {isEnableToCreateOrganization && (
                        <CreateNewOrganizationButton
                            onClick={handleCreateNewOrganization}>
                            <PlusIcon
                                height={15}
                                color={theme.colors.secondaryText}
                            />
                            Create New Organization
                        </CreateNewOrganizationButton>
                    )}
                </PopoverBox>
            </Popover>
        </>
    );
};

export default OrganizationSelect;
